// app/javascript/controllers/ai_invitation_controller.js
import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"
import gsap from "gsap"
import { Turbo } from "@hotwired/turbo-rails"
import { clearPendingInvitationContinuation } from "./support/auth_continuation"

export default class extends Controller {
    static targets = [
        "modal",
        "description",
        "photoUploadable",
        "form",
        "status",
        "statusText",
        "submitButton",
        "submitLabel",
        "spinner",
        "styleOption",
        "colorOption",
        "renderingStyle"
    ]

    static values = {
        createUrl: String,
        currentUserId: Number,
        trialRemaining: Number
    }

    get isLoggedIn() {
        return this.hasCurrentUserIdValue && this.currentUserIdValue > 0
    }

    connect() {
        this.modalInstance = new bootstrap.Modal(this.modalTarget, {
            backdrop: true,
            keyboard: true,
            focus: false
        })

        this._resetState()

        const url = new URL(window.location.href)
        let pending = false
        try {
            pending = window.sessionStorage.getItem("pending_ai_invitation") === "1"
            if (this.isLoggedIn && pending) {
                window.sessionStorage.removeItem("pending_ai_invitation")
                window.sessionStorage.removeItem("pending_ai_return_to")
            }
        } catch (_error) { /* Storage can be disabled. */ }

        if (url.searchParams.get("ai") === "1" || (this.isLoggedIn && pending)) {
            url.searchParams.delete("ai")
            window.history.replaceState(window.history.state, "", url)
            this.openModal()
        }
    }

    disconnect() {
        // ❗ Do NOT call hide() or dispose() here – Turbo may still be in the middle
        // of a transition callback, and disposing the instance makes Bootstrap's
        // `_hideModal` crash when it runs later.
        this.modalInstance = null

        // Best-effort cleanup, but only if there are no other visible modals
        const anyVisibleModal = document.querySelector(".modal.show")
        if (!anyVisibleModal) {
            document.body.classList.remove("modal-open")
            document.body.style.overflow = ""
            document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove())
        }
    }

    _showModalWithFocus() {
        if (!this.modalInstance) return

        this.modalInstance.show()

        // Tiny focus / highlight animation on textarea
        setTimeout(() => {
            if (!this.hasDescriptionTarget) return
            this.descriptionTarget.focus()

            gsap.fromTo(
                this.descriptionTarget,
                { boxShadow: "0 0 0 0 rgba(13,110,253,0)" },
                {
                    boxShadow: "0 0 0 4px rgba(13,110,253,0.28)",
                    duration: 0.4,
                    yoyo: true,
                    repeat: 1,
                    onComplete: () => {
                        this.descriptionTarget.style.boxShadow = "none"
                    }
                }
            )
        }, 200)
    }

    // === UI helpers ===

    openModal(event) {
        event?.preventDefault()

        if (!this.isLoggedIn && this.trialRemainingValue <= 0) {
            this._requireLogin()
            return
        }

        this._resetState()
        this._restoreDraft()
        this._showModalWithFocus()
    }

    _requireLogin() {
        const returnTo = new URL(window.location.href)
        returnTo.searchParams.set("ai", "1")
        try {
            clearPendingInvitationContinuation()
            window.sessionStorage.setItem("pending_ai_invitation", "1")
            window.sessionStorage.setItem("pending_ai_return_to", returnTo.href)
            if (this.descriptionTarget.value.trim()) {
                window.sessionStorage.setItem("pending_ai_draft", JSON.stringify({
                    description: this.descriptionTarget.value,
                    photo: this.photoUploadableTarget.checked,
                    realistic: this.hasRenderingStyleTarget && this.renderingStyleTarget.checked,
                    styles: this.styleOptionTargets.filter(b => b.classList.contains("active")).map(b => b.dataset.styleValue),
                    color: this._currentColorValue()
                }))
            }
        } catch (_error) { /* URL continuation still works without storage. */ }

        const showLogin = () => {
            const login = document.getElementById("loginModal")
            if (!login) {
                window.location.assign(`/login?continue_to=${encodeURIComponent(returnTo.pathname + returnTo.search)}`)
                return
            }
            // Explicit continuation also supports disabled browser storage and
            // replaces any stale template selection in an already-open form.
            login.querySelectorAll('input[name="continue_to"]').forEach(input => {
                input.value = returnTo.pathname + returnTo.search
            })
            bootstrap.Modal.getOrCreateInstance(login).show()
        }
        if (this.modalTarget.classList.contains("show")) {
            this.modalTarget.addEventListener("hidden.bs.modal", showLogin, { once: true })
            this.modalInstance.hide()
        } else {
            showLogin()
        }
    }

    _restoreDraft() {
        try {
            const draft = JSON.parse(window.sessionStorage.getItem("pending_ai_draft") || "null")
            if (!draft || typeof draft.description !== "string") return
            this.descriptionTarget.value = draft.description.slice(0, 2000)
            this.photoUploadableTarget.checked = !!draft.photo
            if (this.hasRenderingStyleTarget) this.renderingStyleTarget.checked = !!draft.realistic
            this.styleOptionTargets.forEach(b => b.classList.toggle("active", Array.isArray(draft.styles) && draft.styles.includes(b.dataset.styleValue)))
            this.colorOptionTargets.forEach(b => b.classList.toggle("active", draft.color === b.dataset.colorValue))
        } catch (_error) { /* Ignore missing or invalid saved input. */ }
    }

    _resetState() {
        if (this.hasDescriptionTarget) {
            this.descriptionTarget.value = ""
        }
        if (this.hasPhotoUploadableTarget) {
            this.photoUploadableTarget.checked = false
        }
        if (this.hasRenderingStyleTarget) {
            this.renderingStyleTarget.checked = false
        }
        if (this.hasStatusTextTarget) {
            this.statusTextTarget.textContent = ""
        }
        if (this.hasStatusTarget) {
            this.statusTarget
                .querySelector(".status-dot")
                ?.classList.remove("is-busy", "is-done", "is-error")
        }
        if (this.hasSpinnerTarget) {
            this.spinnerTarget.classList.add("d-none")
        }
        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = false
        }
        if (this.hasSubmitLabelTarget) {
            this.submitLabelTarget.textContent = "Generate my invitation"
        }
    }

    toggleStyle(event) {
        event.preventDefault()
        if (!this.hasStyleOptionTarget) return
        // Instead of clearing all, toggle the clicked button
        event.currentTarget.classList.toggle("active")
    }

    toggleColor(event) {
        event.preventDefault()
        if (!this.hasColorOptionTarget) return
        this.colorOptionTargets.forEach((btn) => btn.classList.remove("active"))
        event.currentTarget.classList.add("active")
    }

    _currentStyleValue() {
        if (!this.hasStyleOptionTarget) return null
        // Collect all active style chips and join their values
        const activeButtons = this.styleOptionTargets.filter((b) =>
            b.classList.contains("active")
        )
        if (activeButtons.length === 0) return null
        const values = activeButtons.map((b) => b.dataset.styleValue)
        return values.join(", ")
    }

    _currentColorValue() {
        if (!this.hasColorOptionTarget) return null
        const btn = this.colorOptionTargets.find((b) => b.classList.contains("active"))
        return btn?.dataset.colorValue || null
    }

    // === Form submit ===

    async submit(event) {
        event.preventDefault()
        if (!this.hasFormTarget || this.submitting) return

        const description     = this.descriptionTarget.value.trim()
        const photoUploadable = this.photoUploadableTarget.checked
        const style           = this._currentStyleValue()
        const colorMood       = this._currentColorValue()
        const renderingStyle = (this.hasRenderingStyleTarget && this.renderingStyleTarget.checked)
            ? "realistic"
            : null

        if (!description) {
            this._setError("Please describe your invitation first.")
            return
        }

        this.submitting = true
        this._setBusy("Designing your invitation… You can keep browsing while we work.")

        try {
            const response = await fetch(this.createUrlValue, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "X-CSRF-Token": this._csrfToken()
                },
                body: JSON.stringify({
                    description:      description,
                    photo_uploadable: photoUploadable,
                    style:            style,
                    color_mood:       colorMood,
                    rendering_style:  renderingStyle
                })
            })

            const payload = await response.json()
            if (response.status === 401 && payload.login_required) {
                this._setError(payload.error)
                this._requireLogin()
                return
            }
            if (!response.ok) throw new Error(payload.error || "Could not start your invitation. Please try again.")

            try { window.sessionStorage.removeItem("pending_ai_draft") } catch (_error) { /* No storage. */ }

            // Close the modal – panel will flip into "Creating something magical…" via Turbo Stream
            if (this.modalInstance && this.modalTarget && this.modalTarget.isConnected) {
                this.modalInstance.hide()
            }

            // Always restore scrollability (Turbo can interrupt Bootstrap’s transition)
            document.body.classList.remove("modal-open")
            document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove())
            document.body.style.overflow = ""
            if (payload.panel) Turbo.renderStreamMessage(payload.panel)

        } catch (error) {
            console.error("[AI Invitation] submit error", error)
            this._setError(
                error.message || "Something went wrong while starting your invitation. Please try again."
            )
        } finally {
            this.submitting = false
        }
    }

    // === Status helpers ===

    _setBusy(text) {
        if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("d-none")
        if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true
        if (this.hasSubmitLabelTarget)
            this.submitLabelTarget.textContent = "Working…"

        if (this.hasStatusTextTarget) this.statusTextTarget.textContent = text
        if (this.hasStatusTarget) {
            const dot = this.statusTarget.querySelector(".status-dot")
            if (dot) {
                dot.classList.remove("is-done", "is-error")
                dot.classList.add("is-busy")
            }
        }
    }

    _setDone(text) {
        if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("d-none")
        if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = false
        if (this.hasSubmitLabelTarget)
            this.submitLabelTarget.textContent = "View invitation"

        if (this.hasStatusTextTarget) this.statusTextTarget.textContent = text
        if (this.hasStatusTarget) {
            const dot = this.statusTarget.querySelector(".status-dot")
            if (dot) {
                dot.classList.remove("is-busy", "is-error")
                dot.classList.add("is-done")
            }
        }
    }

    _setError(text) {
        if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("d-none")
        if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = false
        if (this.hasSubmitLabelTarget)
            this.submitLabelTarget.textContent = "Try again"

        if (this.hasStatusTextTarget) this.statusTextTarget.textContent = text
        if (this.hasStatusTarget) {
            const dot = this.statusTarget.querySelector(".status-dot")
            if (dot) {
                dot.classList.remove("is-busy", "is-done")
                dot.classList.add("is-error")
            }
        }
    }

    _csrfToken() {
        const meta = document.querySelector("meta[name='csrf-token']")
        return meta && meta.getAttribute("content")
    }
}
